import { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { Tabs } from '@dgshahr/ui-kit'
import { useButtonActionsContext } from '@/utils/contexts/form-layout/ButtonActionsContext'
import FormHeader from '@/components/layouts/form-layout/FormHeader'
import {
  individualTabsContentFactory,
  individualUploadTabs,
  legalTabsContentFactory,
  legalUploadTabs,
  uploaderTabs
} from '@/data/merchant-register-forms/DocumentUploadStep'
import { useNavigate } from 'react-router-dom'
import {
  IDocumentsFormValues,
  TCorrectionMappedData,
  TUploadedFile,
  TUploadType
} from '@/types/uploader'
import { useGetDocuments } from '@/api/get-documents/GetDocuments'
import { useRegisterMerchantDocuments } from '@/api/register-merchant-docs/RegisterDocuments'
import { useGetStepCorrections } from '@/api/step-correction/StepCorrection'
import { IGetCorrectionDocumentResponse } from '@/api/step-correction/type'
import { useUpdateRegisterMerchantDocuments } from '@/api/update-register-docs/UpdateRegisterDocuments'
import { createDocumentDynamicSchema } from '@/utils/validations/document-upload/uploadDocumentSchema'
import useAuthStore from '@/store/useAuthStore'

const mapCorrectionData = (data: IGetCorrectionDocumentResponse[]) => {
  const mappedData: TCorrectionMappedData = {} as TCorrectionMappedData

  data?.forEach((item) => {
    const { document_type } = item
    mappedData[document_type] = item
  })
  return mappedData
}

const DocumentUploadForm = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<number>(1)
  const [uploadType, setUploadType] = useState<TUploadType>('')
  const { setButtonActions } = useButtonActionsContext()
  const { setLastStep } = useAuthStore()

  const { data } = useGetStepCorrections({ step: 'DOCUMENT' })
  const correction: IGetCorrectionDocumentResponse[] = data
  const isHaveCorrection = Boolean(correction?.length)

  const { data: documents, isFetching: isFetchingDocuments } = useGetDocuments()
  const {
    mutate: registerDocumentStep,
    isSuccess: isSuccessRegister,
    isPending: isPendingRegister
  } = useRegisterMerchantDocuments()
  const {
    mutate: updateRegisterDocumentStep,
    isSuccess: isSuccessUpdateRegister,
    isPending: isPendingUpdateRegister
  } = useUpdateRegisterMerchantDocuments()

  const convertBackDataToFormValues = (
    dataType: keyof IDocumentsFormValues
  ) => {
    if (!documents?.documents.length) return
    const files = documents?.documents
      .filter((doc) => doc.document_type === dataType)
      .map(
        (doc) =>
          ({
            id: doc.id,
            src: doc.download_url,
            title: doc.name,
            loading: false
          }) as TUploadedFile
      )

    return files
  }

  const formik = useFormik<IDocumentsFormValues & { uploadType: TUploadType }>({
    initialValues: {
      BUSINESS_LOGO: convertBackDataToFormValues('BUSINESS_LOGO') ?? [],
      NATIONAL_ID_CARD: convertBackDataToFormValues('NATIONAL_ID_CARD') ?? [],
      NATIONAL_ID_CARD_BACK_SIDE: convertBackDataToFormValues('NATIONAL_ID_CARD_BACK_SIDE') ?? [],
      BIRTH_CERTIFICATE: convertBackDataToFormValues('BIRTH_CERTIFICATE') ?? [],
      BUSINESS_LICENSE: convertBackDataToFormValues('BUSINESS_LICENSE') ?? [],
      TAX_REGISTRATION_CERTIFICATE:
        convertBackDataToFormValues('TAX_REGISTRATION_CERTIFICATE') ?? [],
      OFFICIAL_NEWSPAPER:
        convertBackDataToFormValues('OFFICIAL_NEWSPAPER') ?? [],
      COMPANY_ARTICLES_OF_ASSOCIATION:
        convertBackDataToFormValues('COMPANY_ARTICLES_OF_ASSOCIATION') ?? [],
      uploadType: documents?.is_individual ? 'individual' : 'legal'
    },
    validateOnMount: false,
    enableReinitialize: true,
    validate: (values) => {
      const schema = createDocumentDynamicSchema(values)
      const result = schema.safeParse(values)

      if (result.success) {
        return {}
      } else {
        const errors: { [key: string]: string } = {}
        result.error.issues.forEach((issue) => {
          const field = issue.path.at(0)?.toString() ?? 'form'
          errors[field] = issue.message
        })
        return errors
      }
    },
    onSubmit: () => {
      if (isHaveCorrection) {
        updateRegisterDocumentStep()
      } else {
        registerDocumentStep()
      }
    }
  })

  useEffect(() => {
    setUploadType(formik.values.uploadType)
  }, [formik.values.uploadType])

  useEffect(() => {
    if (isSuccessRegister) {
      setLastStep(4)
      navigate('/merchant-register/contract')
    }
    if (isSuccessUpdateRegister) {
      navigate('/merchant-register/status')
    }
  }, [isSuccessRegister, isSuccessUpdateRegister])

  useEffect(() => {
    setButtonActions((prevState) => ({
      ...prevState,
      prevStepDisabled: false,
      prevStepAction: isHaveCorrection
        ? () => {
            navigate('/merchant-register/status')
          }
        : () => {
            navigate('/merchant-register/address')
          },
      nextStepDisabled: isPendingRegister || isPendingUpdateRegister,
      nextStepIsLoading: isPendingRegister || isPendingUpdateRegister,
      prevStepLabel: isHaveCorrection ? 'انصراف' : 'مرحله قبلی',
      nextStepLabel: isHaveCorrection ? 'ذخیره' : 'مرحله بعدی',
      nextStepAction: formik.handleSubmit
    }))
  }, [
    formik.isValid,
    formik.handleSubmit,
    setButtonActions,
    isPendingRegister,
    correction
  ])

  useEffect(() => {
    const errorKeys = Object.keys(formik.errors)
    const shouldScrollToError =
      formik.isSubmitting && !formik.isValidating && errorKeys.length > 0

    if (!shouldScrollToError) return

    const errorTabs = errorKeys
      .map((key) => uploaderTabs[key as keyof typeof uploaderTabs])
      .filter((tab): tab is number => typeof tab === 'number')

    if (errorTabs.length === 0 || errorTabs.includes(activeTab)) return

    const firstErrorTab = [...new Set(errorTabs)].sort((a, b) => a - b)[0]
    setActiveTab(firstErrorTab)
  }, [formik.errors, formik.isSubmitting, formik.isValidating, activeTab])

  return (
    <div className="document-upload-form">
      <FormHeader title="بارگذاری مدارک" />

      <form onSubmit={formik.handleSubmit} className="space-y-6">
        <label
          htmlFor="uploadType"
          className="flex gap-1 font-p2-medium text-right text-gray-600 mb-4"
        >
          <span>
            لطفا تصاویر لازم در هر یک از بخش‌های زیر را بارگذاری کنید.
          </span>
          <span className="text-error-500">*</span>
        </label>

        {uploadType && (
          <Tabs
            className="overflow-x-auto !w-full scrollbar-hidden desktop:max-w-[715px]"
            tabItemClassName="text-nowrap"
            activeKey={activeTab}
            items={
              uploadType === 'individual'
                ? individualUploadTabs
                : legalUploadTabs
            }
            onChange={(key) => setActiveTab(Number(key))}
          />
        )}
        {isFetchingDocuments ? (
          <div className="flex my-16 items-center justify-center min-h-72">
            <div className="custome-dot-flashing" />
          </div>
        ) : (
          <div>
            {uploadType === 'individual' && (
              <div className="mt-8 px-2 ss02">
                {
                  individualTabsContentFactory(
                    formik,
                    true,
                    mapCorrectionData(correction)
                  )[activeTab]
                }
              </div>
            )}
            {uploadType === 'legal' && (
              <div className="mt-8 px-2 ss02">
                {
                  legalTabsContentFactory(
                    formik,
                    false,
                    mapCorrectionData(correction)
                  )[activeTab]
                }
              </div>
            )}
          </div>
        )}
      </form>
    </div>
  )
}

export default DocumentUploadForm
